import type { ReactNode } from "react";
import { Card } from "../../components/ui";

export function Stat({ label, value, cls = "", hint }: { label: string; value: ReactNode; cls?: string; hint?: string }) {
  return (
    <Card className="px-4 py-3">
      <div className={`font-heading text-2xl font-semibold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      {hint && <div className="mt-0.5 text-[11px] text-brand-grey">{hint}</div>}
    </Card>
  );
}

export function SectionCard({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
        <h2 className="font-heading text-base font-semibold">{title}</h2>
        {aside && <span className="text-xs text-brand-grey">{aside}</span>}
      </div>
      {children}
    </Card>
  );
}

/** Money in the compact form the labour screens use throughout: CDF runs to nine figures here, and a table of
 * full amounts is unreadable at a glance. The exact figure stays in the title attribute. */
export function money(value: number, lng: string) {
  const abs = Math.abs(value);
  const compact = abs >= 1e6 ? `${(value / 1e6).toFixed(2)} M` : abs >= 1e3 ? `${(value / 1e3).toFixed(0)} k` : `${value.toFixed(0)}`;
  return { compact: `${compact} CDF`, full: new Intl.NumberFormat(lng === "fr" ? "fr-CD" : "en-GB").format(Math.round(value)) };
}

export function Money({ value, lng, cls = "" }: { value: number; lng: string; cls?: string }) {
  const m = money(value, lng);
  return <span className={`tabular-nums ${cls}`} title={`${m.full} CDF`}>{m.compact}</span>;
}

/** A bar showing one row's share of the heaviest row, so a rollup reads without the reader doing division. */
export function Share({ value, of }: { value: number; of: number }) {
  const pct = of > 0 ? Math.max(2, Math.round((value / of) * 100)) : 0;
  return (
    <div className="h-1.5 w-full rounded bg-brand-grey-light">
      <div className="h-1.5 rounded bg-brand-primary" style={{ width: `${pct}%` }} />
    </div>
  );
}
